import React, {Fragment, useEffect} from 'react';
import {gql} from 'apollo-boost';
import {useQuery, useMutation} from '@apollo/react-hooks';
import {Button, Card, NonIdealState} from '@blueprintjs/core';
import {useHistory, useParams} from 'react-router-dom';
import {Breadcrumbs, Loading} from 'worksource-ui';
import mixpanel from 'mixpanel-browser';

import ShareFilesBar from 'components/ShareFilesBar';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import withMainLayout from 'layouts/main/view';
import FilePreview from 'components/FilePreview';

const TOGGLE_FILE_SELECTION = gql`
  mutation($file: SelectedFile) {
    toggleFileSelection(file: $file) @client
  }
`;

const SET_SELECTING_FILES = gql`
  mutation($value: Boolean!) {
    setSelectingFiles(value: $value) @client
  }
`;

const FETCH_FAVORITES = gql`
  query GuidesFavoritesPageFetch($clientId: ID!, $guideId: ID!) {
    viewerState @client {
      selectingFiles,
      selectedFiles {
        id,
      },
    },
    guides(clientId: $clientId, id: $guideId) {
      name,
    }
    favorites(clientId: $clientId, guideId: $guideId) {
      file {
        id,
        name,
        url,
        thumbnailUrl,
      }
    }
  }
`;

const GuidesFavoritesPage = () => {
  ////
  // Hooks
  //
  const {clientId, guideId} = useParams();
  const history = useHistory();

  useEffect(() => {
    mixpanel.track('Open favorites viewer', {clientId, guideId});
  }, []);

  ////
  // GraphQL
  //
  const [toggleFileSelection] = useMutation(TOGGLE_FILE_SELECTION);
  const [setSelectingFiles] = useMutation(SET_SELECTING_FILES);
  const {loading: loadingFetch, data} = useQuery(FETCH_FAVORITES, {
    variables: {clientId, guideId},
    onError: error => showGQLError({error}),
  });

  ////
  // Actions
  //
  const openFile = ({file}) => {
    if (data.viewerState.selectingFiles) {
      toggleFileSelection({variables: {file}});
      return;
    }

    history.push(`/clients/${clientId}/guides/${guideId}/files/${file.id}?favorites=true`);
  };

  ////
  // Render
  //
  if (!data || loadingFetch) {
    return (
      <Loading />
    );
  }

  const {
    guides: [guide],
    favorites,
    viewerState: {selectingFiles, ...viewerState},
  } = data;
  const selectedFileIds = new Set(viewerState.selectedFiles.map(file => file.id));

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides`}
            icon="book"
            text="Guides"
          />
          <Breadcrumbs.Breadcrumb
            href={`/clients/${clientId}/guides/${guideId}`}
            text={guide.name}
          />
          <Breadcrumbs.Breadcrumb
            current
            href={`/clients/${clientId}/guides/${guideId}/favorites`}
            text="Favorites"
          />
        </Breadcrumbs>
        <div className="spacer" />
        {!selectingFiles && (
          <Button
            icon="multi-select"
            text="Select files"
            onClick={() => setSelectingFiles({variables: {value: true}})}
          />
        )}
      </HeaderBar>

      <ShareFilesBar />

      <div className="content-container">
        <div className="layout--full">
          <Card className="pages-show--files">
            {favorites.map(({file}) => {
              return (
                <FilePreview
                  selected={selectedFileIds.has(file.id)}
                  file={file}
                  key={file.id}
                  onClick={openFile}
                />
              );
            })}
            {favorites.length === 0 && (
              <NonIdealState
                icon="search"
                title="Nothing here"
                description="You don't have any favorites yet"
              />
            )}
          </Card>
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout({viewerMenu: true})(GuidesFavoritesPage);
